<template>
  <div class="page-container work-log-page">
    <div class="card">
      <div class="tabs-header-wrapper">
        <div class="page-title">工作日志管理</div>
        <div class="action-btn-group">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">新增</el-button>
          <el-button v-if="isManager" size="small" @click="reconcile">刷新当天工作日志</el-button>
          <el-button v-if="isManager" type="success" size="small" @click="confirmAll">确认全部</el-button>
          <el-button size="small" icon="el-icon-download" @click="exportLogs">导出</el-button>
        </div>
      </div>

      <div class="search-bar">
        <el-date-picker
          v-model="query.logDate"
          type="date"
          value-format="yyyy-MM-dd"
          :clearable="false"
          :picker-options="{ disabledDate: disableFuture }"
          style="width: 160px"
          @change="search"
        />
        <el-select
          v-model="query.ownerUserId"
          clearable
          filterable
          placeholder="工作人员"
          style="width: 150px"
          @change="search"
        >
          <el-option
            v-for="worker in workers"
            :key="worker.userCode"
            :label="worker.capacityName"
            :value="worker.userCode"
          />
        </el-select>
        <AreaCascader
          v-model="query.zoneAreaId"
          :options="areaTree"
          placeholder="所属大区"
          clearable
          style="width: 180px"
          @change="search"
        />
        <el-select v-model="query.status" clearable placeholder="状态" style="width: 120px" @change="search">
          <el-option label="待确认" value="draft" />
          <el-option label="已确认" value="confirmed" />
        </el-select>
        <el-select v-model="query.sourceType" clearable placeholder="来源" style="width: 120px" @change="search">
          <el-option label="任务" value="task" />
          <el-option label="故障" value="fault" />
          <el-option label="手工" value="manual" />
        </el-select>
        <el-select v-model="query.sourceAction" clearable placeholder="来源动作" style="width: 140px" @change="search">
          <el-option v-for="action in sourceActions" :key="action.value" :label="action.label" :value="action.value" />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" @click="search">搜索</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="reset">重置</el-button>
      </div>

      <div class="table-wrapper">
        <el-table
          v-loading="loading"
          :data="rows"
          border
          stripe
          style="width: 100%"
          height="100%"
          :header-cell-style="{ textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
        >
          <el-table-column prop="ownerUserName" label="主要执行人" min-width="100" />
          <el-table-column label="来源动作" min-width="110">
            <template slot-scope="{ row }">
              <span class="source-action">{{ row.sourceActionName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="工作类型" min-width="70">
            <template slot-scope="{ row }">
              <span class="work-type-tag" :class="'work-type-tag--' + workTypeTheme(row.workType)">
                {{ formatWorkType(row.workType) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="zoneAreaName" label="所属大区" min-width="80" />
          <el-table-column prop="secondaryAreaName" label="具体二级区域" min-width="120" />
          <el-table-column prop="workContent" label="工作内容" min-width="220" show-overflow-tooltip />
          <el-table-column label="实际发生时间" min-width="160">
            <template slot-scope="{ row }">{{ formatDateTime(row.actualTime) }}</template>
          </el-table-column>
          <el-table-column label="是否更换材料" min-width="90">
            <template slot-scope="{ row }">{{ row.materialReplacedFlag === 'Y' ? '已更换' : '未更换' }}</template>
          </el-table-column>
          <el-table-column label="处理标记" min-width="160">
            <template slot-scope="{ row }">{{ formatFaultCreatedMark(row) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="80">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="tagType(row.status)">{{ row.statusName }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220" fixed="right">
            <template slot-scope="{ row }">
              <el-button size="mini" @click="openDetail(row)">详情</el-button>
              <el-button v-if="canEdit(row)" type="primary" plain size="mini" @click="openEdit(row)">编辑</el-button>
              <el-button
                v-if="canOperate(row) && row.status === 'draft'"
                type="success"
                plain
                size="mini"
                @click="confirm(row)"
              >
                确认
              </el-button>
              <el-button v-if="canDelete(row)" type="danger" plain size="mini" @click="remove(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <el-pagination
        class="pagination"
        :current-page="query.pageNo"
        :page-sizes="[10, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="changePageSize"
        @current-change="changePage"
      />
    </div>

    <WorkLogDialog v-if="dialogVisible" :visible.sync="dialogVisible" :log-id="activeId" @success="load" />

    <WorkLogDetailDialog v-if="detailVisible" :visible.sync="detailVisible" :log-id="detailId" />
    <el-dialog
      title="导出工作日志"
      :visible.sync="exportDialogVisible"
      width="420px"
      :close-on-click-modal="false"
      append-to-body
      custom-class="work-log-export-dialog"
    >
      <p class="export-dialog-hint">选择 {{ query.logDate }} 需要导出的人员日志</p>
      <p class="export-dialog-hint export-dialog-hint--notice">
        <i class="el-icon-warning-outline" />
        仅有已确认日志的人员可勾选导出
      </p>
      <el-checkbox-group v-model="exportUserIds" class="export-user-list">
        <el-checkbox
          v-for="user in dayUsers"
          :key="user.ownerUserId"
          :label="user.ownerUserId"
          :disabled="Number(user.confirmedCount || 0) === 0"
          class="export-user-checkbox"
        >
          {{ user.ownerUserName || user.ownerUserId }}（已确认 {{ user.confirmedCount || 0 }} 条，待确认
          {{ user.draftCount || 0 }} 条）
        </el-checkbox>
      </el-checkbox-group>
      <div slot="footer">
        <el-button @click="exportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitExport">导出</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { HMStoreService } from 'kestrel-core-ui';
import { formatDate, formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import { downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';
import { isDataManager } from '@/features/common/utils/gnmkRoles.js';
import { areaService } from '@/features/gnmk/area/area.service.js';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';
import WorkLogDialog from './workLogDialog.vue';
import WorkLogDetailDialog from './workLogDetailDialog.vue';
import { workLogService } from './work-log.service.js';
import { normalizeProcessType, formatWorkType, workTypeTheme } from './workLogDict.js';

const createQuery = () => ({
  logDate: formatDate(new Date()),
  ownerUserId: '',
  zoneAreaId: '',
  status: '',
  sourceType: '',
  sourceAction: '',
  pageNo: 1,
  pageSize: 20
});

export default {
  name: 'WorkLogList',
  components: { WorkLogDialog, WorkLogDetailDialog, AreaCascader },
  data () {
    return {
      loading: false,
      rows: [],
      total: 0,
      dialogVisible: false,
      exportDialogVisible: false,
      dayUsers: [],
      exportUserIds: [],
      activeId: null,
      detailVisible: false,
      detailId: null,
      workers: [],
      areaTree: [],
      sourceActions: [
        { label: '任务正常', value: 'task_normal' },
        { label: '任务异常', value: 'task_abnormal' },
        { label: '发现故障', value: 'fault_discover' },
        { label: '故障已上报', value: 'fault_report' },
        { label: '故障维修', value: 'fault_repair' },
        { label: '手工记录', value: 'manual' },
        { label: '日常任务完成', value: 'daily_task_completed' }
      ],
      query: createQuery()
    };
  },
  computed: {
    isManager () {
      return isDataManager();
    },
    account () {
      return (HMStoreService.getUserinfo() || {}).account || '';
    }
  },
  methods: {
    formatDateTime,
    formatWorkType,
    workTypeTheme,
    formatFaultCreatedMark (row) {
      const processType = normalizeProcessType(row);
      if (processType === 'normal') return '普通记录';
      const label = processType === 'repair_completed' ? '故障维修完成' : '新增故障';
      return row.faultCode ? `${label}：${row.faultCode}` : label;
    },
    async load () {
      this.loading = true;
      try {
        const res = await workLogService.queryPage(this.query);
        const page = res.data || {};
        this.rows = page.rows || page.list || [];
        this.total = page.total || 0;
      } finally {
        this.loading = false;
      }
    },
    async loadFilterOptions () {
      const [workersResult, areaResult] = await Promise.all([
        workLogService.workerCandidates(),
        areaService.getAreaTree()
      ]);
      this.workers = workersResult.data || [];
      this.areaTree = (areaResult.data || []).map(({ children, ...area }) => area);
    },
    search () {
      this.query.pageNo = 1;
      this.load();
    },
    reset () {
      this.query = createQuery();
      this.load();
    },
    changePageSize (pageSize) {
      this.query.pageSize = pageSize;
      this.query.pageNo = 1;
      this.load();
    },
    changePage (page) {
      this.query.pageNo = page;
      this.load();
    },
    tagType (status) {
      if (status === 'confirmed') return 'success';
      return 'warning';
    },
    canOperate (row) {
      return this.isManager || row.ownerUserId === this.account;
    },
    canEdit (row) {
      return this.isManager || (row.ownerUserId === this.account && row.status === 'draft');
    },
    canDelete (row) {
      return this.canOperate(row) && row.status === 'draft';
    },
    disableFuture (date) {
      return date.getTime() > new Date().setHours(23, 59, 59, 999);
    },
    openCreate () {
      this.activeId = null;
      this.dialogVisible = true;
    },
    openEdit (row) {
      this.activeId = row.uuid;
      this.dialogVisible = true;
    },
    openDetail (row) {
      this.detailId = row.uuid;
      this.detailVisible = true;
    },
    async confirm (row) {
      try {
        await this.$confirm('确认该日志吗？', '确认日志', { type: 'warning' });
      } catch (error) {
        return;
      }
      await workLogService.confirm([row.uuid]);
      this.$message.success('确认成功');
      this.load();
    },
    async reconcile () {
      await workLogService.reconcileDeptDay({ logDate: this.query.logDate });
      this.$message.success('补偿完成');
      this.load();
    },
    async confirmAll () {
      try {
        await this.$confirm('确认当前筛选条件下的所有待确认日志吗？', '批量确认日志', { type: 'warning' });
      } catch (error) {
        return;
      }
      const result = await workLogService.confirmAll({
        logDate: this.query.logDate,
        ownerUserId: this.query.ownerUserId,
        status: this.query.status,
        zoneAreaId: this.query.zoneAreaId,
        sourceType: this.query.sourceType,
        sourceAction: this.query.sourceAction
      });
      const count = Number(result.data == null ? result : result.data) || 0;
      this.$message.success(`已确认 ${count} 条日志`);
      this.load();
    },
    async exportLogs () {
      const res = await workLogService.dayUsers(this.query.logDate);
      this.dayUsers = res.data || [];
      this.exportUserIds = this.dayUsers
        .filter(user => Number(user.confirmedCount) > 0)
        .map(user => user.ownerUserId);
      this.exportDialogVisible = true;
    },
    async submitExport () {
      if (!this.exportUserIds.length) {
        this.$message.warning('请至少选择一名人员');
        return;
      }
      const draftCount = this.getSelectedDraftCount();
      if (draftCount > 0 && !await this.confirmExportWithDrafts(draftCount)) return;
      const res = await workLogService.export({
        logDate: this.query.logDate,
        ownerUserIds: this.exportUserIds
      });
      downloadFromApiBlobResponse(res, `工作日志-${this.query.logDate}.xlsx`);
      this.exportDialogVisible = false;
    },
    getSelectedDraftCount () {
      return this.dayUsers
        .filter(user => this.exportUserIds.includes(user.ownerUserId))
        .reduce((sum, user) => sum + Number(user.draftCount || 0), 0);
    },
    async confirmExportWithDrafts (draftCount) {
      try {
        await this.$confirm(
          `当前选择人员仍有 ${draftCount} 条待确认记录，这些记录不会进入文档，是否继续导出？`,
          '导出提示',
          { type: 'warning' }
        );
        return true;
      } catch (error) {
        return false;
      }
    },
    async remove (row) {
      try {
        await this.$confirm('删除后不可恢复，确认删除该日志吗？', '确认删除', { type: 'warning' });
      } catch (error) {
        return;
      }
      await workLogService.remove(row.uuid);
      this.$message.success('删除成功');
      this.load();
    }
  },
  created () {
    this.loadFilterOptions();
    this.load();
  }
};
</script>

<style lang="scss" scoped>
.work-log-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  background: #f5f7fa;

  .card {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    padding: 10px 20px;
    box-sizing: border-box;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .tabs-header-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 48px;
    margin-bottom: 4px;
    border-bottom: 1px solid #e4e7ed;
  }

  .page-title {
    padding: 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }

  .action-btn-group {
    position: absolute;
    top: 50%;
    right: 0;
    display: flex;
    gap: 8px;
    transform: translateY(-50%);
  }

  .search-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 12px 0;
    margin-bottom: 10px;
    border-bottom: 1px solid #f0f0f0;
  }

  .table-wrapper {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .pagination {
    flex-shrink: 0;
    padding: 12px 0 4px;
    text-align: right;
  }

  .source-action {
    color: #1890ff;
  }

  /* 与小程序 workLogList.vue 的 .work-type 保持同一套色板，rpx 按 750 设计稿折半换算。 */
  .work-type-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 18px;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.2;
  }

  .work-type-tag--wb {
    background: #e6f7ff;
    color: #1890ff;
  }

  .work-type-tag--cs {
    background: #fff7e6;
    color: #ff9800;
  }

  .work-type-tag--xc {
    background: #f0f9eb;
    color: #67c23a;
  }

  .work-type-tag--ls {
    background: #fff7e6;
    color: #fa8c16;
  }

  .work-type-tag--zyrw {
    background: #fff1f0;
    color: #f5222d;
    font-weight: 600;
  }

  .work-type-tag--default {
    background: #f5f5f5;
    color: #999;
  }
}

/* 弹窗挂载到 body 后不再位于 .work-log-page 内，样式需直接绑定弹窗根节点。 */
.work-log-export-dialog {
  .el-dialog__body {
    padding: 18px 20px 10px;
  }

  .export-dialog-hint {
    margin: 0 0 10px;
    color: #909399;
    font-size: 13px;
    line-height: 20px;
  }

  .export-dialog-hint--notice {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    padding: 8px 10px;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    background: #fffbe6;
    color: #d48806;
  }

  .export-user-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 0 4px;
  }

  .export-user-checkbox {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 11px 4px;
    border-bottom: 1px solid #f0f0f0;

    .el-checkbox__label {
      overflow: hidden;
      padding-left: 10px;
      color: #303133;
      line-height: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
